/**
 * Generic surface mesh pass. packages/surface produces SurfaceBuffers; the engine colours each vertex from its owning
 * atom (SurfaceBuilder.cs:318-334: vertex colour = colour of VertexToAtom, alpha = opacity). Opaque when opacity >= 1,
 * otherwise drawn after opaques as back faces then front faces (SurfaceRenderer.cs:93-112). Wireframe draws edges.
 */
import { BufferAttribute, BufferGeometry, LineSegments, Mesh, type Object3D } from 'three';
import type { SurfaceBuffers } from '@nanome-lite/state';
import type { MeshMaterials } from './ribbon-pass';
import { RENDER_ORDER } from './impostors';
import { encodePickId, pickIdToBytes } from './pick-id';

/** The mesh shape is the host contract in packages/state/src/engine.ts (packages/surface produces it). */
export type { SurfaceBuffers };

/** Unique undirected edges of a triangle list, for wireframe mode. */
export function edgeIndex(triangles: Uint32Array): Uint32Array {
  const seen = new Set<number>();
  const out: number[] = [];
  const add = (a: number, b: number) => {
    const lo = Math.min(a, b), hi = Math.max(a, b);
    const key = lo * 4194304 + hi; // 22-bit vertex indices fit a double exactly
    if (seen.has(key)) return;
    seen.add(key); out.push(lo, hi);
  };
  for (let i = 0; i < triangles.length; i += 3) {
    const a = triangles[i]!, b = triangles[i + 1]!, c = triangles[i + 2]!;
    add(a, b); add(b, c); add(c, a);
  }
  return Uint32Array.from(out);
}

export class SurfaceMesh {
  readonly geometry: BufferGeometry;
  readonly objects: Object3D[] = [];
  readonly pick: Mesh;
  /** shadow caster; null for wireframe and transparent surfaces */
  readonly shadow: Mesh | null = null;
  readonly transparent: boolean;
  private readonly owned: { dispose(): void }[] = [];

  constructor(buffers: SurfaceBuffers, colorOfAtom: (atomIndex: number) => [number, number, number], materials: MeshMaterials, pickSlot: number) {
    const n = buffers.position.length / 3;
    const color = new Float32Array(n * 3);
    const pick = new Uint8Array(n * 4);
    for (let i = 0; i < n; i++) {
      const c = colorOfAtom(buffers.atomIndex[i]!);
      color[i * 3] = c[0]; color[i * 3 + 1] = c[1]; color[i * 3 + 2] = c[2];
      pickIdToBytes(encodePickId(pickSlot, buffers.atomIndex[i]!), pick, i * 4);
    }
    const g = new BufferGeometry();
    g.setAttribute('position', new BufferAttribute(buffers.position, 3));
    g.setAttribute('normal', new BufferAttribute(buffers.normal, 3));
    g.setAttribute('color', new BufferAttribute(color, 3));
    g.setAttribute('aPick', new BufferAttribute(pick, 4, true));
    g.setAttribute('aHighlight', new BufferAttribute(new Float32Array(n), 1));
    g.setIndex(new BufferAttribute(buffers.triangles, 1));
    this.geometry = g;
    this.transparent = !buffers.wireframe && buffers.opacity < 1;

    const mk = (obj: Mesh | LineSegments, order: number) => { obj.frustumCulled = false; obj.matrixAutoUpdate = false; obj.renderOrder = order; this.objects.push(obj); return obj; };
    if (buffers.wireframe) {
      const wg = new BufferGeometry();
      wg.setAttribute('position', g.getAttribute('position'));
      wg.setAttribute('normal', g.getAttribute('normal'));
      wg.setAttribute('color', g.getAttribute('color'));
      wg.setAttribute('aPick', g.getAttribute('aPick'));
      wg.setAttribute('aHighlight', g.getAttribute('aHighlight'));
      wg.setIndex(new BufferAttribute(edgeIndex(buffers.triangles), 1));
      this.owned.push(wg);
      mk(new LineSegments(wg, materials.surfaceWire), RENDER_ORDER.opaqueMesh);
    } else if (this.transparent) {
      const { back, front } = materials.transparentPair(buffers.opacity);
      this.owned.push(back, front);
      mk(new Mesh(g, back), RENDER_ORDER.transparentBack);
      mk(new Mesh(g, front), RENDER_ORDER.transparentFront);
    } else {
      mk(new Mesh(g, materials.surfaceLitOpaque), RENDER_ORDER.opaqueMesh);
    }
    if (!buffers.wireframe && !this.transparent) {
      // SurfaceRenderer.cs:109 casts two-sided, but only Lit_Texture (opaque) has a ShadowCaster pass;
      // Lit_TextureTransparent.shadergraph sets m_CastShadows false. Every surface receives.
      const shadow = new Mesh(g, materials.surfaceShadow);
      shadow.frustumCulled = false; shadow.matrixAutoUpdate = false;
      this.shadow = shadow;
    }
    this.pick = new Mesh(g, materials.pick);
    this.pick.frustumCulled = false; this.pick.matrixAutoUpdate = false;
    // Transparent surfaces do not pick (the atoms behind them do), matching Nanome's picker which ignores surfaces.
    this.pick.visible = false;
  }

  dispose(): void {
    this.geometry.dispose();
    for (const o of this.owned) o.dispose();
  }
}
